import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import useTable from '@/hooks/useTable';
import type { GsEvent } from '@/lib/types';
import { formatEventDate } from '@/lib/format';
import DemoBadge from '@/components/feature/DemoBadge';

type Row = {
  key: string;
  name: string;
  course: string;
  city: string;
  date: string;
  status: 'Published' | 'Draft';
  local: boolean;
};

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-3.5 py-2.5 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1.5';

export default function TournamentManager() {
  const { data: events } = useTable<GsEvent>('gs_events');
  const [added, setAdded] = useState<Row[]>([]);
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [flash, setFlash] = useState('');
  const [showForm, setShowForm] = useState(false);

  const seeded: Row[] = useMemo(
    () => events.map((e) => ({
      key: `db-${e.id}`,
      name: e.name,
      course: e.course,
      city: e.city,
      date: e.event_date || '',
      status: 'Published' as const,
      local: false,
    })),
    [events],
  );

  const rows = useMemo(() => [...added, ...seeded].filter((r) => !removed.has(r.key)), [added, seeded, removed]);

  const toggle = (key: string) => {
    setAdded((prev) => prev.map((r) => (r.key === key ? { ...r, status: r.status === 'Published' ? 'Draft' : 'Published' } : r)));
    setSeededFlash(key);
  };
  const setSeededFlash = (key: string) => {
    if (key.startsWith('db-')) {
      setFlash('That listing lives in the Backend — toggling it here is a visual preview only and does not change the live site.');
      setTimeout(() => setFlash(''), 4000);
    }
  };

  const addTournament = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const row: Row = {
      key: `local-${Date.now()}`,
      name: String(data.get('name') || 'Untitled tournament'),
      course: String(data.get('course') || ''),
      city: String(data.get('city') || ''),
      date: String(data.get('date') || ''),
      status: 'Draft',
      local: true,
    };
    setAdded((prev) => [row, ...prev]);
    setFlash('Draft added below. In the live product it would save to the Backend and appear on the calendar once published.');
    setTimeout(() => setFlash(''), 4000);
    form.reset();
    setShowForm(false);
  };

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Tournaments</h2>
        <DemoBadge label="Demo — not saved" />
        <button
          type="button"
          onClick={() => setShowForm((s) => !s)}
          className="ml-auto inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className={showForm ? 'ri-close-line' : 'ri-add-line'}></i></span>
          {showForm ? 'Close' : 'New tournament'}
        </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {showForm && (
        <div className="xl:col-span-5">
          <form onSubmit={addTournament} className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading font-bold text-xl text-foreground-950">Add a golf tournament</h3>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label htmlFor="t-name" className={labelClass}>Tournament name</label>
                <input id="t-name" name="name" required placeholder="e.g. Sample Charity Scramble" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="t-course" className={labelClass}>Course</label>
                <input id="t-course" name="course" placeholder="Course name" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="t-city" className={labelClass}>City</label>
                <input id="t-city" name="city" placeholder="City" className={fieldClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="t-date" className={labelClass}>Date</label>
                <input id="t-date" name="date" type="date" className={fieldClass} />
              </div>
            </div>
            <button type="submit" className="mt-4 w-full bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider py-3 rounded-md whitespace-nowrap cursor-pointer transition-colors">
              Add tournament
            </button>
          </form>
        </div>
        )}

        <div className={showForm ? 'xl:col-span-7' : 'xl:col-span-12'}>
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-background-200 flex items-center justify-between">
              <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">{rows.length} listings</p>
              <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">Review &amp; publish</p>
            </div>
            <ul className="divide-y divide-background-200 max-h-[420px] overflow-y-auto">
              {rows.map((r) => (
                <li key={r.key} className="px-5 py-3.5 flex items-center gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground-950 truncate">{r.name}</p>
                    <p className="text-xs text-foreground-500 truncate">
                      {[r.course, r.city].filter(Boolean).join(', ')}{r.date ? ` \u00b7 ${formatEventDate(r.date)}` : ''}
                    </p>
                  </div>
                  <span className={`font-label font-semibold uppercase text-[10px] tracking-wide px-2.5 py-1 rounded-sm whitespace-nowrap ${r.status === 'Published' ? 'bg-primary-500 text-background-50' : 'bg-accent-100 text-accent-900 border border-accent-300'}`}>{r.status}</span>
                  <button type="button" onClick={() => toggle(r.key)} className="font-label uppercase tracking-wide text-[11px] font-semibold text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap">
                    {r.status === 'Published' ? 'Unpublish' : 'Publish'}
                  </button>
                  <button type="button" onClick={() => setRemoved((prev) => new Set(prev).add(r.key))} aria-label="Remove" className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-secondary-700 hover:bg-background-100 cursor-pointer">
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {flash && (
        <p className="mt-4 bg-secondary-50 border border-secondary-200 text-secondary-900 text-sm rounded-md px-4 py-3 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-information-line"></i></span>{flash}
        </p>
      )}
    </section>
  );
}